import Decimal from "decimal.js";
import { ReactNode, createContext, useContext, useMemo } from "react";
import { useEnvironmentConfig } from "./environment.hooks";

const DEFAULT_DECIMALS = 6;
const DEFAULT_EMPTY = "--";

const parseDecimalPlaces = (value: unknown): number => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return DEFAULT_DECIMALS;
  }
  return Math.floor(num);
};

type DecimalConfigContextType = {
  unitsDecimalPlaces: number;
  navDecimalPlaces: number;
  formatUnits: (
    value?: Decimal | string | number | null,
    fallback?: string,
  ) => string;
  formatNavPrice: (
    value?: Decimal | string | number | null,
    fallback?: string,
  ) => string;
};

const DecimalConfigContext = createContext<DecimalConfigContextType | null>(
  null,
);

const formatDecimal = (
  value: Decimal | string | number | null | undefined,
  decimalPlaces: number,
  fallback = DEFAULT_EMPTY,
  useThousandsSeparator = false,
) => {
  if (value === undefined || value === null) return fallback;
  if (value === "--" || value === "-") return value;
  if (typeof value === "string" && value.trim() === "") return fallback;

  try {
    const decimalValue = value instanceof Decimal ? value : new Decimal(value);
    const result = decimalValue.toFixed(decimalPlaces);
    return useThousandsSeparator
      ? result.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : result;
  } catch {
    return fallback;
  }
};

export const DecimalConfigProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { config } = useEnvironmentConfig();
  const runtimeConfig = config as
    | {
        unitsDecimalPlaces?: number | null;
        navDecimalPlaces?: number | null;
      }
    | undefined;

  const contextValue = useMemo(() => {
    const unitsDecimalPlaces = parseDecimalPlaces(
      runtimeConfig?.unitsDecimalPlaces,
    );
    const navDecimalPlaces = parseDecimalPlaces(
      runtimeConfig?.navDecimalPlaces,
    );

    return {
      unitsDecimalPlaces,
      navDecimalPlaces,
      formatUnits: (
        value?: Decimal | string | number | null,
        fallback = DEFAULT_EMPTY,
      ) => formatDecimal(value, unitsDecimalPlaces, fallback),
      formatNavPrice: (
        value?: Decimal | string | number | null,
        fallback = DEFAULT_EMPTY,
      ) => formatDecimal(value, navDecimalPlaces, fallback),
    };
  }, [runtimeConfig?.navDecimalPlaces, runtimeConfig?.unitsDecimalPlaces]);

  return (
    <DecimalConfigContext.Provider value={contextValue}>
      {children}
    </DecimalConfigContext.Provider>
  );
};

export const useDecimalConfig = () => {
  const context = useContext(DecimalConfigContext);
  if (!context) {
    throw new Error(
      "useDecimalConfig must be used within a DecimalConfigProvider",
    );
  }
  return context;
};
